import React, { useState, useEffect } from 'react';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Venue, Subscription, Location } from '@prisma/client';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { prisma } from '../db';
import { localeFiles } from '../data/constants';
import { Tier, tiers } from '../constants/prices';
import { ENVIRONMENT } from '../env.server';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '../components/atoms/Button';
import VenuePackages from '../components/organisms/VenuePackages';
import PageDefault from '../components/templates/PageDefault';
import UpgradeHelper from '../components/molecules/UpgradeHelper';
import Prompt from '../components/molecules/Prompt';
import { serializePrismaObject } from '../lib/utils';

interface PageProps {
  venue?: Venue & {
    subscriptions: Subscription[];
    location: Location;
  };
  prices: Tier[];
}

const Page = ({ venue, prices }: PageProps) => {
  const router = useRouter();
  const [layer, setLayer] = useState() as any;
  const { t } = useTranslation(['common', 'pages']);
  const slug = 'venue-packages';

  useEffect(() => {
    const helperSeen = localStorage.getItem('vs-upgrade-helper');
    const plan = venue?.plan.toLowerCase() || '';
    if (!!router.query.rfp_prompt) {
      setLayer(
        <Prompt onClick={() => setLayer({})}>
          <p>{t(`pages:${slug}:upgrade:rfp-prompt`)}</p>
        </Prompt>
      );
    } else if (['basic', 'gold'].indexOf(plan) + 1 && !helperSeen) {
      setLayer(
        <UpgradeHelper
          h={t(t(`pages:${slug}:upgrade:help-h`))}
          p={t(`pages:${slug}:upgrade:${plan}:help-p`)}
          setLayer={setLayer}
        />
      );
    }
  }, [venue, router, t]);

  return (
    <>
      <Head>
        <title>
          {t(`pages:${slug}:title`)} | {t('app-name')}
        </title>
        <meta name="description" content={t(`pages:${slug}:description`)} />
      </Head>

      <PageDefault layer={layer}>
        <Row>
          <Col>
            <Button
              text
              iconLeft
              label={t('common:cta-back')}
              onClick={() => router.back()}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <VenuePackages
              setLayer={setLayer}
              venue={venue}
              prices={prices || []}
            />
          </Col>
        </Row>
      </PageDefault>
    </>
  );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, query } = context;

  const baseProps = {
    ...(await serverSideTranslations(locale!, localeFiles)),
  };

  const id = query.venue_id ? String(query.venue_id) : '';

  if (!id) {
    return { props: baseProps };
  }

  const venue = await prisma.venue.findUnique({
    where: {
      id,
    },
    include: {
      location: true,
    },
  });

  if (!venue) {
    return { props: baseProps };
  }

  const space = venue.exhibition_indoor_m2 ? venue.exhibition_indoor_m2 : 0;

  const prices = tiers[ENVIRONMENT]?.filter(
    (tier) => tier.type === (space >= 10000 ? 'exhibition' : 'congress')
  );

  return {
    props: {
      ...baseProps,
      venue: serializePrismaObject(venue),
      prices,
    },
  };
}

export default Page;
